/**
 * ONE polite region per page, not one per control.
 *
 * Three regions compete and a screen-reader user hears the same event twice.
 * The shell mounts the single region; every surface announces into it through
 * `useAnnouncer()`.
 *
 * A per-second countdown is plain text and never enters a live region — it
 * would speak once a second and drown everything else.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

interface AnnouncerValue {
  announce: (message: string) => void;
}

const AnnouncerContext = createContext<AnnouncerValue | null>(null);

export function AnnouncerProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState('');

  const announce = useCallback((next: string) => {
    // Re-setting an identical string does not re-announce; the blank cycle is
    // what makes a repeated message audible again.
    setMessage('');
    window.setTimeout(() => setMessage(next), 60);
  }, []);

  const value = useMemo<AnnouncerValue>(() => ({ announce }), [announce]);

  return (
    <AnnouncerContext.Provider value={value}>
      {children}
      <p aria-live="polite" aria-atomic="true" className="visually-hidden">
        {message}
      </p>
    </AnnouncerContext.Provider>
  );
}

export function useAnnouncer(): AnnouncerValue {
  const context = useContext(AnnouncerContext);
  if (!context) throw new Error('useAnnouncer must be used inside <AnnouncerProvider>.');
  return context;
}
